import React, { useState, useEffect } from 'react';
import AuthorizeXeroStep from './AuthorizeXeroStep';
import AskContextualizeQuestionsStep from './AskContextualizeQuestionsStep';
import CreditControllingPoliciesStep from './CreditControllingPoliciesStep';
import UnderstandRubricsStep from './UnderstandRubricsStep';
import WeeklyDunningOpStep from './WeeklyDunningOpStep';
import OnboardingNavigation, { OnboardingStep } from './OnboardingNavigation';

interface PublicOnboardingContainerProps {
  organization: { id: string; legal_entity_name: string };
  onboarding_status: any;
  customers: any[];
  customer_categories: any[];
  governance_overlays: any[];
  ap_archetypes: any[];
  template_messages: any[];
  initialStep?: OnboardingStep;
  showNavigation?: boolean;
  isMobileMenu?: boolean;
  onStepChange?: (step: OnboardingStep) => void;
}

const PublicOnboardingContainer: React.FC<PublicOnboardingContainerProps> = ({ organization, onboarding_status, customers, customer_categories, governance_overlays, ap_archetypes, template_messages, initialStep = 'authorize', showNavigation = true, isMobileMenu = false, onStepChange }) => {
  const [selectedStep, setSelectedStep] = useState<OnboardingStep>(initialStep);
  const [customersInSections, setCustomersInSections] = useState<any[]>([]);
  const [customBehaviors, setCustomBehaviors] = useState<any[]>([]);

  // Update selectedStep when initialStep changes
  useEffect(() => {
    setSelectedStep(initialStep);
  }, [initialStep]);

  // Handle step change
  const handleStepChange = (step: OnboardingStep) => {
    setSelectedStep(step);
    if (onStepChange) {
      onStepChange(step);
    }
  };

  // Callback functions for drag & drop functionality
  const handleUpdateCustomer = (updatedCustomer: any) => {
    // In public mode, we don't actually save changes, just update local state
    console.log('Customer updated (public mode):', updatedCustomer);
  };

  const handleAddCustomBehavior = (behavior: any) => {
    setCustomBehaviors(prev => [...prev, behavior]);
  };

  const handleAddCustomer = (newCustomer: any) => {
    // In public mode, we don't actually add customers
    console.log('Customer added (public mode):', newCustomer);
  };

  const handleRemoveCustomer = (customerId: string) => {
    // In public mode, we don't actually remove customers
    console.log('Customer removed (public mode):', customerId);
  };

  // If this is a mobile menu, only show the navigation
  if (isMobileMenu) {
    return (
      <OnboardingNavigation
        selectedStep={selectedStep}
        onStepChange={handleStepChange}
        onboardingStatus={onboarding_status}
        publicMode={true}
      />
    );
  }

  return (
    <div className={showNavigation ? "grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6" : ""}>
      {showNavigation && (
        <aside className="hidden lg:block lg:col-span-3">
          <OnboardingNavigation
            selectedStep={selectedStep}
            onStepChange={handleStepChange}
            onboardingStatus={onboarding_status}
            publicMode={true}
          />
        </aside>
      )}

      <section className={showNavigation ? "lg:col-span-9" : "w-full"}>
        {selectedStep === 'authorize' && (
          <AuthorizeXeroStep onboardingStatus={onboarding_status} onAuthorizeXero={() => {}} loading={false} publicMode={true} />
        )}
        {selectedStep === 'contextualize' && (
          <AskContextualizeQuestionsStep 
            onboardingStatus={onboarding_status} 
            organizationId={organization.id} 
            governanceOverlays={governance_overlays || []} 
            apArchetypes={ap_archetypes || []} 
            customers={customers || []} 
            customersInSections={customersInSections}
            setCustomersInSections={setCustomersInSections}
            customBehaviors={customBehaviors}
            onUpdateCustomer={handleUpdateCustomer}
            onAddCustomBehavior={handleAddCustomBehavior}
            onAddCustomer={handleAddCustomer}
            onRemoveCustomer={handleRemoveCustomer}
            publicMode={true} 
          />
        )}
        {selectedStep === 'policies' && (
          <CreditControllingPoliciesStep onboardingStatus={onboarding_status} publicMode={true} />
        )}
        {selectedStep === 'rubrics' && (
          <UnderstandRubricsStep 
            organizationId={organization.id} 
            customers={customers || []} 
            customersInSections={customersInSections} 
            governanceOverlays={governance_overlays || []} 
            publicMode={true} 
          />
        )}
        {selectedStep === 'upcoming-dunning' && (
          <WeeklyDunningOpStep 
            customers={customers || []} 
            organizationId={organization.id} 
            customerCategories={customer_categories || []} 
            organization={organization} 
            customersInSections={customersInSections || []} 
            governanceOverlays={governance_overlays || []} 
            templateMessages={template_messages || []} 
            publicMode={true} 
          />
        )}
      </section>
    </div>
  );
};

export default PublicOnboardingContainer;


